/**
 * Create Company/Contact Form Component
 *
 * Reusable form component for creating companies and contacts.
 * Can be used in both Dialog and Page contexts.
 */

import { Loader2, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCompanyCategories, useCreateCompany, useCreateEmployee } from '../hooks';
import type {
  CompanyGroupName,
  ContactType,
  CreateCompanyInput,
  CreateEmployeeInput,
} from '../types';

interface Props {
  contactType: ContactType;
  onSuccess?: (company: any) => void;
  onCancel?: () => void;
  isPending?: boolean;
}

const GROUP_OPTIONS: CompanyGroupName[] = ['Closing', 'Payoffs', 'Services', 'Government', 'Other'];

interface EmployeeInput {
  name: string;
  title: string;
  email: string;
  phone: string;
  is_primary: boolean;
}

const INITIAL_EMPLOYEE: EmployeeInput = {
  name: '',
  title: '',
  email: '',
  phone: '',
  is_primary: false,
};

const INITIAL_FORM: CreateCompanyInput = {
  category_id: '',
  name: '',
  email: '',
  phone: '',
  fax: '',
  website: '',
  primary_contact_name: '',
  primary_contact_title: '',
  primary_contact_email: '',
  address_street: '',
  address_unit: '',
  address_city: '',
  address_state: '',
  address_zip: '',
  address_country: 'USA',
  tags: [],
  internal_notes: '',
  license_number: '',
  license_state: '',
  license_expiry: '',
  nmls_id: '',
};

// Helper component for optional label
function OptionalLabel({ children }: { children: React.ReactNode }) {
  return <span className="text-muted-foreground font-normal"> {children}</span>;
}

export function CreateCompanyForm({
  contactType,
  onSuccess,
  onCancel,
  isPending: externalPending,
}: Props) {
  const [formData, setFormData] = useState<CreateCompanyInput>(INITIAL_FORM);
  const [selectedGroup, setSelectedGroup] = useState<CompanyGroupName | ''>('');
  const [tagsInput, setTagsInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [employees, setEmployees] = useState<EmployeeInput[]>([]);

  const { data: categories } = useCompanyCategories();
  const createCompany = useCreateCompany();
  const createEmployee = useCreateEmployee();

  const isPending = externalPending || createCompany.isPending;

  // Filter categories by selected group
  const filteredCategories = selectedGroup
    ? categories?.filter((c) => c.group_name === selectedGroup)
    : categories;

  // Check if form is valid
  const isFormValid = () => {
    if (!formData.name?.trim() && !formData.primary_contact_name?.trim()) {
      return false;
    }
    if (formData.name?.trim() && !formData.category_id) {
      return false;
    }
    return true;
  };

  // Reset form on mount
  useEffect(() => {
    setFormData(INITIAL_FORM);
    setSelectedGroup('');
    setTagsInput('');
    setErrors({});
    setEmployees([]);
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // For individual contacts, primary contact name is required
    if (contactType === 'individual') {
      if (!formData.primary_contact_name?.trim()) {
        newErrors.general = 'Primary contact name is required for individual contacts';
      }
    } else {
      // For organizations, either company name or primary contact name is required
      if (!formData.name?.trim() && !formData.primary_contact_name?.trim()) {
        newErrors.general = 'Either company name or primary contact name is required';
      }

      // Category is required when company name is provided
      if (formData.name?.trim() && !formData.category_id) {
        newErrors.category_id = 'Category is required when company name is provided';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const data: CreateCompanyInput = {
      ...formData,
      contact_type: contactType,
      tags,
      category_id: formData.name?.trim() ? formData.category_id : undefined,
    };

    Object.keys(data).forEach((key) => {
      if (
        typeof data[key as keyof CreateCompanyInput] === 'string' &&
        !(data[key as keyof CreateCompanyInput] as string)
      ) {
        delete data[key as keyof CreateCompanyInput];
      }
    });

    try {
      const result = await createCompany.mutateAsync(data);

      // Create additional employees if any
      const validEmployees = employees.filter((emp) => emp.name.trim().length >= 2);
      for (const employee of validEmployees) {
        try {
          const employeeData: CreateEmployeeInput = {
            name: employee.name,
            is_primary: employee.is_primary,
          };
          if (employee.title) employeeData.title = employee.title;
          if (employee.email) employeeData.email = employee.email;
          if (employee.phone) employeeData.phone = employee.phone;

          await createEmployee.mutateAsync({
            companyId: result.id,
            data: employeeData,
          });
        } catch (empErr) {
          console.error('Failed to create employee:', empErr);
        }
      }

      onSuccess?.(result);
      setFormData(INITIAL_FORM);
      setSelectedGroup('');
      setTagsInput('');
      setErrors({});
      setEmployees([]);
    } catch (err) {
      console.error('Failed to save company:', err);
    }
  };

  const handleInputChange = (field: keyof CreateCompanyInput, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field] || errors.general) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        delete newErrors.general;
        return newErrors;
      });
    }
  };

  const handleGroupChange = (group: CompanyGroupName) => {
    setSelectedGroup(group);
    setFormData((prev) => ({ ...prev, category_id: '' }));
  };

  // Employee management functions
  const addEmployee = () => {
    setEmployees((prev) => [...prev, { ...INITIAL_EMPLOYEE }]);
  };

  const removeEmployee = (index: number) => {
    setEmployees((prev) => prev.filter((_, i) => i !== index));
  };

  const updateEmployee = (index: number, field: keyof EmployeeInput, value: string | boolean) => {
    setEmployees((prev) => prev.map((emp, i) => (i === index ? { ...emp, [field]: value } : emp)));
  };

  const hasCompanyName = !!formData.name?.trim();
  const categoryRequired = hasCompanyName && contactType === 'organization';
  const primaryContactRequired = !hasCompanyName && contactType === 'organization';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.general && (
        <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20">
          <p className="text-sm text-destructive">{errors.general}</p>
        </div>
      )}

      {/* Company Details - Hide for individual contacts */}
      {contactType === 'organization' && (
        <div className="border border-border/40 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-border/40 bg-muted/20">
            <h3 className="text-sm font-medium text-foreground">Company Details</h3>
          </div>
          <div className="p-5 space-y-5">
            <div className="grid gap-5 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label htmlFor="name" className="text-sm font-medium">
                  Company Name
                  {!primaryContactRequired && <OptionalLabel>(optional)</OptionalLabel>}
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., ABC Title Company"
                  className={errors.name ? 'border-destructive' : 'mt-2'}
                />
                {!hasCompanyName && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Provide a company name or add a primary contact below
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="group" className="text-sm font-medium">
                  Group{categoryRequired && <span className="text-destructive">*</span>}
                </Label>
                <Select
                  value={selectedGroup}
                  onValueChange={handleGroupChange}
                  disabled={!formData.name?.trim()}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select group" />
                  </SelectTrigger>
                  <SelectContent>
                    {GROUP_OPTIONS.map((group) => (
                      <SelectItem key={group} value={group}>
                        {group}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="category_id" className="text-sm font-medium">
                  Category{categoryRequired && <span className="text-destructive">*</span>}
                </Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) => handleInputChange('category_id', value)}
                  disabled={!selectedGroup || !formData.name?.trim()}
                >
                  <SelectTrigger
                    className={`mt-2 ${errors.category_id ? 'border-destructive' : ''}`}
                  >
                    <SelectValue
                      placeholder={
                        !formData.name?.trim()
                          ? 'Enter company name first'
                          : selectedGroup
                            ? 'Select category'
                            : 'Select group first'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredCategories?.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.category_id && (
                  <p className="text-xs text-destructive mt-2">{errors.category_id}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Company Contact - Hide for individual contacts */}
      {contactType === 'organization' && (
        <div className="border border-border/40 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-border/40 bg-muted/20">
            <h3 className="text-sm font-medium text-foreground">Company Contact</h3>
          </div>
          <div className="p-5">
            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <Label htmlFor="email" className="text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="company@example.com"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-sm font-medium">
                  Phone
                </Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="fax" className="text-sm font-medium">
                  Fax
                </Label>
                <Input
                  id="fax"
                  value={formData.fax}
                  onChange={(e) => handleInputChange('fax', e.target.value)}
                  placeholder="(555) 123-4568"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="website" className="text-sm font-medium">
                  Website
                </Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="www.example.com"
                  className="mt-2"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Primary Contact - Always show, but make required for individual contacts */}
      <div className="border border-border/40 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-border/40 bg-muted/20">
          <h3 className="text-sm font-medium text-foreground">
            Primary Contact
            {primaryContactRequired && <span className="text-destructive ml-1">*</span>}
          </h3>
        </div>
        <div className="p-5">
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <Label htmlFor="primary_contact_name" className="text-sm font-medium">
                Name{primaryContactRequired && <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="primary_contact_name"
                value={formData.primary_contact_name}
                onChange={(e) => handleInputChange('primary_contact_name', e.target.value)}
                placeholder="e.g., John Smith"
                className={`mt-2 ${errors.primary_contact_name ? 'border-destructive' : ''}`}
              />
              {!primaryContactRequired && (
                <p className="text-xs text-muted-foreground mt-2">
                  Optional when company name is provided
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="primary_contact_title" className="text-sm font-medium">
                Title
              </Label>
              <Input
                id="primary_contact_title"
                value={formData.primary_contact_title}
                onChange={(e) => handleInputChange('primary_contact_title', e.target.value)}
                placeholder="e.g., Account Manager"
                className="mt-2"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="primary_contact_email" className="text-sm font-medium">
                Email
              </Label>
              <Input
                id="primary_contact_email"
                type="email"
                value={formData.primary_contact_email}
                onChange={(e) => handleInputChange('primary_contact_email', e.target.value)}
                placeholder="john.smith@example.com"
                className="mt-2"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Additional Employees - Hide for individual contacts */}
      {contactType === 'organization' && (
        <div className="border border-border/40 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-border/40 bg-muted/20 flex items-center justify-between">
            <h3 className="text-sm font-medium text-foreground">Additional Employees</h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addEmployee}
              className="h-7 text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Employee
            </Button>
          </div>
          <div className="p-5">
            {employees.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No additional employees. Click "Add Employee" to add more contacts.
              </p>
            ) : (
              <div className="space-y-4">
                {employees.map((employee, index) => (
                  <div key={index} className="border border-border/30 rounded-lg p-4 relative">
                    <button
                      type="button"
                      onClick={() => removeEmployee(index)}
                      className="absolute top-3 right-3 text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>

                    <div className="grid gap-4 md:grid-cols-2 pr-6">
                      <div className="md:col-span-2">
                        <Label htmlFor={`employee_name_${index}`} className="text-sm font-medium">
                          Name *
                        </Label>
                        <Input
                          id={`employee_name_${index}`}
                          value={employee.name}
                          onChange={(e) => updateEmployee(index, 'name', e.target.value)}
                          placeholder="e.g., Jane Smith"
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor={`employee_title_${index}`} className="text-sm font-medium">
                          Title
                        </Label>
                        <Input
                          id={`employee_title_${index}`}
                          value={employee.title}
                          onChange={(e) => updateEmployee(index, 'title', e.target.value)}
                          placeholder="e.g., Loan Officer"
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor={`employee_email_${index}`} className="text-sm font-medium">
                          Email
                        </Label>
                        <Input
                          id={`employee_email_${index}`}
                          type="email"
                          value={employee.email}
                          onChange={(e) => updateEmployee(index, 'email', e.target.value)}
                          placeholder="jane.smith@example.com"
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor={`employee_phone_${index}`} className="text-sm font-medium">
                          Phone
                        </Label>
                        <Input
                          id={`employee_phone_${index}`}
                          value={employee.phone}
                          onChange={(e) => updateEmployee(index, 'phone', e.target.value)}
                          placeholder="(555) 987-6543"
                          className="mt-2"
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`employee_primary_${index}`}
                          checked={employee.is_primary}
                          onChange={(e) => updateEmployee(index, 'is_primary', e.target.checked)}
                          className="h-4 w-4 rounded border-border/50"
                        />
                        <Label
                          htmlFor={`employee_primary_${index}`}
                          className="text-sm cursor-pointer"
                        >
                          Set as primary contact
                        </Label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Address */}
      <div className="border border-border/40 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-border/40 bg-muted/20">
          <h3 className="text-sm font-medium text-foreground">Address</h3>
        </div>
        <div className="p-5">
          <div className="grid gap-5 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label htmlFor="address_street" className="text-sm font-medium">
                Street Address
              </Label>
              <Input
                id="address_street"
                value={formData.address_street}
                onChange={(e) => handleInputChange('address_street', e.target.value)}
                placeholder="123 Main Street"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="address_unit" className="text-sm font-medium">
                Unit/Suite
              </Label>
              <Input
                id="address_unit"
                value={formData.address_unit}
                onChange={(e) => handleInputChange('address_unit', e.target.value)}
                placeholder="Apt 4B"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="address_city" className="text-sm font-medium">
                City
              </Label>
              <Input
                id="address_city"
                value={formData.address_city}
                onChange={(e) => handleInputChange('address_city', e.target.value)}
                placeholder="Denver"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="address_state" className="text-sm font-medium">
                State
              </Label>
              <Input
                id="address_state"
                value={formData.address_state}
                onChange={(e) => handleInputChange('address_state', e.target.value)}
                placeholder="CO"
                maxLength={2}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="address_zip" className="text-sm font-medium">
                ZIP Code
              </Label>
              <Input
                id="address_zip"
                value={formData.address_zip}
                onChange={(e) => handleInputChange('address_zip', e.target.value)}
                placeholder="80202"
                className="mt-2"
              />
            </div>
          </div>
        </div>
      </div>

      {/* License Information - Hide for individual contacts */}
      {contactType === 'organization' && (
        <div className="border border-border/40 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-border/40 bg-muted/20">
            <h3 className="text-sm font-medium text-foreground">License Information</h3>
          </div>
          <div className="p-5">
            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <Label htmlFor="license_number" className="text-sm font-medium">
                  License Number
                </Label>
                <Input
                  id="license_number"
                  value={formData.license_number}
                  onChange={(e) => handleInputChange('license_number', e.target.value)}
                  placeholder="e.g., 123456"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="license_state" className="text-sm font-medium">
                  License State
                </Label>
                <Input
                  id="license_state"
                  value={formData.license_state}
                  onChange={(e) => handleInputChange('license_state', e.target.value)}
                  placeholder="CO"
                  maxLength={2}
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="license_expiry" className="text-sm font-medium">
                  License Expiry
                </Label>
                <Input
                  id="license_expiry"
                  type="date"
                  value={formData.license_expiry}
                  onChange={(e) => handleInputChange('license_expiry', e.target.value)}
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="nmls_id" className="text-sm font-medium">
                  NMLS ID
                </Label>
                <Input
                  id="nmls_id"
                  value={formData.nmls_id}
                  onChange={(e) => handleInputChange('nmls_id', e.target.value)}
                  placeholder="e.g., 123456"
                  className="mt-2"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Additional Information */}
      <div className="border border-border/40 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-border/40 bg-muted/20">
          <h3 className="text-sm font-medium text-foreground">Additional Information</h3>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <Label htmlFor="tags" className="text-sm font-medium">
              Tags <OptionalLabel>(optional)</OptionalLabel>
            </Label>
            <Input
              id="tags"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="e.g., preferred, local, fast-closing"
              className="mt-2"
            />
            <p className="text-xs text-muted-foreground mt-2">Separate multiple tags with commas</p>
          </div>

          <div>
            <Label htmlFor="internal_notes" className="text-sm font-medium">
              Internal Notes <OptionalLabel>(optional)</OptionalLabel>
            </Label>
            <Textarea
              id="internal_notes"
              value={formData.internal_notes}
              onChange={(e) => handleInputChange('internal_notes', e.target.value)}
              placeholder="Private notes about this company or contact..."
              rows={3}
              className="mt-2 resize-none"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isPending || !isFormValid()} className="min-w-[160px]">
          {isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : contactType === 'individual' ? (
            'Add Individual'
          ) : (
            'Add Organization'
          )}
        </Button>
      </div>

      {/* Help text */}
      <div className="border border-border/40 rounded-lg bg-muted/20 p-4">
        <p className="text-sm font-medium text-foreground mb-2">Required fields:</p>
        <ul className="space-y-1 text-sm text-muted-foreground">
          {contactType === 'individual' ? (
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>
                <strong>Primary Contact Name</strong> (required for individual contacts)
              </span>
            </li>
          ) : (
            <>
              <li className="flex items-start gap-2">
                <span className="text-muted-foreground mt-0.5">•</span>
                <span>
                  Either <strong>Company Name</strong> OR <strong>Primary Contact Name</strong> (or
                  both)
                </span>
              </li>
              {hasCompanyName && (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground mt-0.5">•</span>
                    <span>
                      <strong>Group</strong> (required when company name is provided)
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground mt-0.5">•</span>
                    <span>
                      <strong>Category</strong> (required when company name is provided)
                    </span>
                  </li>
                </>
              )}
              {!hasCompanyName && (
                <li className="flex items-start gap-2">
                  <span className="text-muted-foreground mt-0.5">•</span>
                  <span>
                    <strong>Primary Contact Name</strong> (required for contact-only entries)
                  </span>
                </li>
              )}
            </>
          )}
        </ul>
      </div>
    </form>
  );
}

CreateCompanyForm.displayName = 'CreateCompanyForm';
